/**
 * Cliente API de recursos (Sprint 15 S15-Z, ajustado en S17-22a).
 *
 * Cubre GET/PATCH/DELETE + ciclo de vida (cambiar-estado, asignar,
 * devolver) para la pagina /tesoreria/recursos. La creacion del recurso
 * la realiza el flujo nuevo Sprint 17 (asignar_recurso desde stock con
 * solicitud aprobada y cobrada).
 */
import { apiFetch } from './client';
import type {
  EstadoRecurso,
  MotivoBaja,
  RecursoPorDesprogramarRead,
  RecursoRead,
  TipoRecurso,
} from './types';

export interface RecursoUpdate {
  identificador?: string | null;
  // Sprint 22: `residente_actual_id` ya NO se edita por PATCH (el backend
  // responde 422 con `extra="forbid"`). El portador se cambia con `asignar` y
  // `liberar`, que validan los invariantes y escriben el historial.
  costo_reposicion?: number;
  entregado_at?: string | null;
  disponible_entrega_at?: string | null;
  notas?: string | null;
  ingreso_vario_id?: string | null;
  // Solo llaveros: se enruta al StockRecurso enlazado en el backend.
  serial_rfid?: string | null;
}

export interface ListRecursosFiltros {
  lote_id?: string;
  tipo?: TipoRecurso;
  estado?: EstadoRecurso;
  residente_actual_id?: string;
}

export function listRecursos(
  filtros: ListRecursosFiltros = {},
): Promise<RecursoRead[]> {
  const params = new URLSearchParams();
  if (filtros.lote_id) params.set('lote_id', filtros.lote_id);
  if (filtros.tipo) params.set('tipo', filtros.tipo);
  if (filtros.estado) params.set('estado', filtros.estado);
  if (filtros.residente_actual_id)
    params.set('residente_actual_id', filtros.residente_actual_id);
  const qs = params.toString();
  return apiFetch<RecursoRead[]>(`/recursos/${qs ? `?${qs}` : ''}`);
}

export function getRecurso(id: string): Promise<RecursoRead> {
  return apiFetch<RecursoRead>(`/recursos/${id}`);
}

export function actualizarRecurso(
  id: string,
  data: RecursoUpdate,
): Promise<RecursoRead> {
  return apiFetch<RecursoRead>(`/recursos/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export function borrarRecurso(id: string): Promise<void> {
  return apiFetch<void>(`/recursos/${id}`, { method: 'DELETE' });
}

export interface AsignarRecursoMePayload {
  residente_id: string;
  /** Obligatorio cuando el recurso es control remoto (P2-B2.2): el CR abre
   * la barrera para un auto específico. Para llavero/otro se omite. */
  vehiculo_id?: string | null;
}

/** El vecino asigna un recurso de la bolsa de su casa a un residente
 * (llavero) o a un residente + vehículo (control remoto). Gate matriz P4
 * en el backend. */
export function asignarRecursoMe(
  recursoId: string,
  data: AsignarRecursoMePayload,
): Promise<RecursoRead> {
  return apiFetch<RecursoRead>(`/me/recursos/${recursoId}/asignar`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// 'de_baja' se alcanza desde 'entregado' (baja sin reposición, 2026-10-06) y
// desde 'reportado' (S19 cabo #4). Exige `motivo_baja`; `motivo` es el
// detalle, obligatorio solo si el motivo es 'otro'.
export type CambioEstadoManual = 'devuelto' | 'perdido' | 'de_baja';

export interface CambiarEstadoPayload {
  nuevo_estado: CambioEstadoManual;
  motivo_baja?: MotivoBaja;
  motivo?: string;
}

/** Llaveros/controles dados de baja que falta borrar de motores y puertas. */
export function listarPorDesprogramar(): Promise<RecursoPorDesprogramarRead[]> {
  return apiFetch<RecursoPorDesprogramarRead[]>('/recursos/por-desprogramar');
}

/** El tesorero declara que ya borró el recurso de todos los equipos. */
export function marcarDesprogramado(
  id: string,
  nota: string | null,
): Promise<RecursoRead> {
  return apiFetch<RecursoRead>(`/recursos/${id}/marcar-desprogramado`, {
    method: 'POST',
    body: JSON.stringify({ nota }),
  });
}

export function cambiarEstado(
  id: string,
  data: CambiarEstadoPayload,
): Promise<RecursoRead> {
  return apiFetch<RecursoRead>(`/recursos/${id}/cambiar-estado`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/** El tesorero asigna un recurso de la bolsa a un residente de la casa.
 *  `vehiculo_id` es obligatorio para control remoto (P2-B2.2).
 *
 *  Sprint 22: pasa a ser la vía por la que la pantalla /tesoreria/recursos
 *  asigna. Antes la pantalla mutaba `residente_actual_id` con el PATCH, que no
 *  validaba nada (ni pertenencia a la casa) y no dejaba historial; este
 *  endpoint valida los invariantes y abre la fila de historial. */
export function asignar(
  id: string,
  residente_id: string,
  vehiculo_id?: string | null,
): Promise<RecursoRead> {
  return apiFetch<RecursoRead>(`/recursos/${id}/asignar`, {
    method: 'POST',
    body: JSON.stringify({ residente_id, vehiculo_id: vehiculo_id ?? null }),
  });
}

/** Causas de dominio por las que un recurso vuelve a la bolsa. Espejo de
 *  `MOTIVOS_LIBERACION` (app/models/asignacion_recurso.py). Las que no están
 *  acá las dispara el sistema solo (baja de residente, cierre de tenencia,
 *  baja de vehículo, estado terminal), no el tesorero a mano. */
export type MotivoLiberacion =
  | 'reemplazo'
  | 'correccion_carga_inicial'
  | 'correccion_tesorero';

export interface LiberarRecursoPayload {
  motivo: MotivoLiberacion;
  /** Obligatorio cuando motivo='correccion_tesorero'. */
  notas?: string | null;
}

/** Devuelve el recurso a la bolsa de la casa y cierra su fila de historial
 *  con el motivo. Reemplaza al viejo `/devolver`, que liberaba sin motivo ni
 *  rastro y no tenía UI que lo llamara. */
export function liberar(
  id: string,
  data: LiberarRecursoPayload,
): Promise<RecursoRead> {
  return apiFetch<RecursoRead>(`/recursos/${id}/liberar`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}
